import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import { useProductBrowser } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { BasicButton } from '../../styles/button';
import { useToggleCollapsibleFilters } from '../../utils/useToggleCollapsibleFilters';

import BottomSheet from './BottomSheet';

const Button = styled(BasicButton)<{
  index?: number;
  onClick: () => void;
  children: JSX.Element;
  alternate?: boolean;
  secondary?: boolean;
  disabled?: boolean;
}>`
  flex: 1;
  width: 100%;
  min-width: 170px;

  &:disabled {
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.black900};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

export default function FilterDrawerV2({ children, handleAppliedFilters }) {
  const {
    setFilterDrawerOpen,
    isFilterDrawerOpen,
    totalFilterCount,
    filters,
    totalCount,
    setFilters,
    baseFilters,
  } = useProductBrowser();
  const openFilters = useToggleCollapsibleFilters({ initialFilters: filters });
  const sheetRef = useRef(null);
  const [showClearAll, setShowClearAll] = useState(totalFilterCount !== 0);

  const onClose = useCallback(() => {
    setFilterDrawerOpen(false);
    setShowClearAll(totalFilterCount !== 0);
    openFilters();
    if (handleAppliedFilters) {
      handleAppliedFilters(filters);
    }
  }, [
    filters,
    handleAppliedFilters,
    openFilters,
    setFilterDrawerOpen,
    totalFilterCount,
  ]);

  const onClearAll = useCallback(async () => {
    await setFilters(prev => ({
      ...prev,
      aggregationFilter: {
        ...baseFilters.aggregationFilter,
      },
    }));
    sheetRef.current?.close();
  }, [setFilters, baseFilters]);

  const title = useMemo(
    () => (
      <div>
        <FormattedMessage
          id="site_product_grid.filter_and_sort_title"
          defaultMessage="Filter & Sort"
        />
        {totalFilterCount && totalFilterCount > 0
          ? ` (${totalFilterCount})`
          : ''}
      </div>
    ),
    [totalFilterCount]
  );

  useEffect(() => {
    if (isFilterDrawerOpen && !sheetRef.current?.isOpened) {
      sheetRef.current?.open();
    }
  }, [isFilterDrawerOpen]);

  useEffect(() => {
    openFilters();
  }, [openFilters]);

  const handleApplyFilters = useCallback(() => {
    onClose();
    sheetRef.current?.close();
  }, [onClose, sheetRef]);

  return (
    <div data-autotag="filter-overlay">
      <BottomSheet
        footer={
          <ButtonWrapper>
            {showClearAll ? (
              <Button
                onClick={onClearAll}
                secondary
                disabled={totalFilterCount === 0}
              >
                <FormattedMessage
                  id="site_product_grid.clear_all"
                  defaultMessage="Clear all"
                />
              </Button>
            ) : null}
            <Button onClick={handleApplyFilters} alternate>
              {showClearAll ? (
                <FormattedMessage
                  id="site_product_grid.show_result_count"
                  defaultMessage="Show {count}"
                  values={{
                    count: totalCount && totalCount > 0 ? `${totalCount}` : '0',
                  }}
                />
              ) : (
                <FormattedMessage
                  id="site_product_grid.show_count_products_button"
                  defaultMessage="Show {count} {count, plural, one {product} other {products}}"
                  values={{
                    count: totalCount && totalCount > 0 ? `${totalCount}` : '0',
                  }}
                />
              )}
            </Button>
          </ButtonWrapper>
        }
        title={title}
        ref={sheetRef}
        onClose={onClose}
        scrollToTopOnOpen
      >
        {children}
      </BottomSheet>
    </div>
  );
}
